import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Search } from 'lucide-react';
import { AppShell, PageSection } from '@/components/app/AppShell';
import { adminApi } from '@/lib/admin';

/** Drill an admin activity row to the object it touched, via its workspace. */
function adminTargetHref(workspaceId: string | null, kind: string | null, id: string | null): string | null {
  if (!workspaceId || !kind) return null;
  const ws = `/app/workspace/${workspaceId}`;
  switch (kind) {
    case 'forecast': return `${ws}/forecast`;
    case 'workbook': return id ? `${ws}/workbook/${id}` : `${ws}/workbooks`;
    case 'entity': return `${ws}/entities`;
    case 'fx_rate': return `${ws}/fx-rates`;
    case 'variance': return ws;
    case 'board_pack': return `${ws}/board-pack`;
    case 'approval': return `${ws}/approvals`;
    case 'data_source': return `${ws}/connectors`;
    default: return null;
  }
}

/**
 * Editorial system-wide admin. Three sections:
 *   - Stat strip (users / companies / workspaces / MTD AI spend / runs)
 *   - Users table (search + paginate, with toggle for is_admin / is_active)
 *   - AI spend by model (last 30d, plain table)
 *   - Recent activity (cross-workspace audit log feed)
 *
 * Access-gated on the backend by AdminGuard; the AppShell only renders
 * the nav item when the JWT user has isAdmin = true.
 */
export function AdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const { data: stats } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => adminApi.stats(),
    staleTime: 30_000,
  });

  const { data: users } = useQuery({
    queryKey: ['admin', 'users', q, offset],
    queryFn: () => adminApi.users({ q: q || undefined, offset, limit }),
    staleTime: 15_000,
  });

  const { data: spend = [] } = useQuery({
    queryKey: ['admin', 'spend'],
    queryFn: () => adminApi.aiSpend(),
    staleTime: 60_000,
  });

  const { data: activity = [] } = useQuery({
    queryKey: ['admin', 'activity'],
    queryFn: () => adminApi.activity(60),
    refetchInterval: 30_000,
  });

  const patch = useMutation({
    mutationFn: ({ id, flags }: { id: string; flags: { isAdmin?: boolean; isActive?: boolean } }) =>
      adminApi.patchUser(id, flags),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin'] }),
  });

  const spendByModel = useMemo(() => {
    const m = new Map<string, { runs: number; cents: number }>();
    for (const b of spend) {
      const k = b.model;
      const e = m.get(k) ?? { runs: 0, cents: 0 };
      e.runs += b.runs;
      e.cents += b.cents;
      m.set(k, e);
    }
    return [...m.entries()].sort((a, b) => b[1].cents - a[1].cents);
  }, [spend]);

  return (
    <AppShell
      title={t('admin.title')}
      subtitle={t('admin.subtitle')}
      pageActions={
        <span className="pill-emerald rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]">
          <ShieldCheck className="inline h-3 w-3 mr-1" strokeWidth={2.4} />
          {t('admin.role_badge')}
        </span>
      }
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-10">
        {/* Stat strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 border border-border rounded-md bg-card overflow-hidden">
          <Stat label={t('admin.stat_users')}      value={stats?.users}      />
          <Stat label={t('admin.stat_companies')}  value={stats?.companies}  />
          <Stat label={t('admin.stat_workspaces')} value={stats?.workspaces} />
          <Stat label={t('admin.stat_ai_runs_mtd')} value={stats?.aiRunsMtd} />
          <Stat label={t('admin.stat_ai_spend_mtd')} value={stats ? `$${(stats.aiSpendCentsMtd / 100).toFixed(2)}` : undefined} accent />
        </div>

        {/* Users table */}
        <PageSection
          eyebrow={t('admin.users_eyebrow')}
          title={t('admin.users_title')}
          actions={
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" strokeWidth={2.2} />
              <input
                placeholder={t('admin.users_search_placeholder')}
                value={q}
                onChange={(e) => { setQ(e.target.value); setOffset(0); }}
                className="h-9 pl-8 pr-3 w-64 rounded-md border border-border bg-card text-[13px] focus:outline-none focus:ring-2 focus:ring-foreground/15"
              />
            </div>
          }
        >
          <div className="border border-border rounded-md bg-card overflow-hidden">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-ink-50/70 border-b border-border">
                  {[t('admin.col_user'), t('admin.col_email'), t('admin.col_companies'), t('admin.col_workspaces'), t('admin.col_ai_mtd'), t('admin.col_status'), t('admin.col_actions')].map((h) => (
                    <th key={h} className="text-left px-4 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(users?.rows ?? []).map((u) => (
                  <tr key={u.id} className="hover:bg-ink-50/40">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded bg-foreground text-background text-[11px] font-semibold">
                          {(u.name || u.email || '?').trim()[0]?.toUpperCase()}
                        </span>
                        <span className="font-medium truncate">{u.name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground truncate max-w-[14rem]">{u.email || '—'}</td>
                    <td className="num px-4 py-2.5">{u.company_count}</td>
                    <td className="num px-4 py-2.5">{u.workspace_count}</td>
                    <td className="num px-4 py-2.5">${(u.ai_spend_cents_mtd / 100).toFixed(2)}</td>
                    <td className="px-4 py-2.5">
                      {u.is_admin && (
                        <span className="pill-emerald rounded px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.12em] mr-1">admin</span>
                      )}
                      {!u.is_active && (
                        <span className="pill-slate rounded px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.12em]">disabled</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => patch.mutate({ id: u.id, flags: { isAdmin: !u.is_admin } })}
                          className="text-[12px] text-emerald-700 hover:underline"
                          title={u.is_admin ? t('admin.revoke_admin') : t('admin.grant_admin')}
                        >
                          {u.is_admin ? t('admin.revoke_admin') : t('admin.grant_admin')}
                        </button>
                        <span className="text-muted-foreground/40">·</span>
                        <button
                          onClick={() => patch.mutate({ id: u.id, flags: { isActive: !u.is_active } })}
                          className="text-[12px] text-muted-foreground hover:text-foreground hover:underline"
                        >
                          {u.is_active ? t('admin.disable') : t('admin.enable')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users && users.rows.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">{t('admin.no_users')}</p>
            )}

            {users && users.total > limit && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border text-[12px] text-muted-foreground">
                <span className="num">
                  {t('admin.range', {
                    from: offset + 1,
                    to: Math.min(offset + limit, users.total),
                    total: users.total,
                  })}
                </span>
                <div className="flex gap-1">
                  <button
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    className="h-8 px-3 rounded border border-border hover:bg-foreground/[0.04] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ←
                  </button>
                  <button
                    disabled={offset + limit >= users.total}
                    onClick={() => setOffset(offset + limit)}
                    className="h-8 px-3 rounded border border-border hover:bg-foreground/[0.04] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    →
                  </button>
                </div>
              </div>
            )}
          </div>
        </PageSection>

        {/* AI spend by model */}
        <PageSection eyebrow={t('admin.spend_eyebrow')} title={t('admin.spend_title')}>
          <div className="border border-border rounded-md bg-card overflow-hidden">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-ink-50/70 border-b border-border">
                  <th className="text-left px-4 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{t('admin.col_model')}</th>
                  <th className="text-right px-4 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{t('admin.col_runs')}</th>
                  <th className="text-right px-4 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{t('admin.col_spend')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {spendByModel.map(([model, agg]) => (
                  <tr key={model}>
                    <td className="num px-4 py-2.5">{model}</td>
                    <td className="num px-4 py-2.5 text-right">{agg.runs.toLocaleString()}</td>
                    <td className="num px-4 py-2.5 text-right font-semibold">
                      ${(agg.cents / 100).toFixed(2)}
                    </td>
                  </tr>
                ))}
                {spendByModel.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-sm text-muted-foreground">
                      {t('admin.spend_empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </PageSection>

        {/* Recent activity */}
        <PageSection eyebrow={t('admin.activity_eyebrow')} title={t('admin.activity_title')}>
          <div className="border border-border rounded-md bg-card divide-y divide-border">
            {activity.map((a, i) => (
              <div key={i} className="px-4 py-2.5 flex items-baseline gap-3 text-[12.5px]">
                <span className="num text-muted-foreground w-20 shrink-0">{new Date(a.when).toLocaleTimeString()}</span>
                <span className="font-medium truncate flex-1">
                  {a.actor_email ?? '—'} <span className="text-muted-foreground">{a.action}</span> {a.target_kind && (() => {
                    const href = adminTargetHref(a.workspace_id, a.target_kind, a.target_id);
                    const label = `${a.target_kind}${a.target_id ? `:${a.target_id.slice(0, 8)}` : ''}`;
                    return href
                      ? <Link to={href} className="num text-emerald-700 hover:underline">{label}</Link>
                      : <span className="num text-muted-foreground">{label}</span>;
                  })()}
                </span>
              </div>
            ))}
            {activity.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">{t('admin.activity_empty')}</div>
            )}
          </div>
        </PageSection>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, accent }: { label: string; value: number | string | undefined; accent?: boolean }) {
  return (
    <div className="border-r last:border-r-0 border-border px-5 py-4">
      <p className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{label}</p>
      <p className={'num-display mt-1 text-2xl md:text-3xl font-semibold ' + (accent ? 'text-emerald-700' : 'text-foreground')}>
        {value === undefined ? '—' : value.toLocaleString?.() ?? value}
      </p>
    </div>
  );
}
